import React from 'react';
import { ExternalLink, TrendingUp, Shield, Cloud, CreditCard, Users, Database } from 'lucide-react';

const ProjectsSection = () => {
  const projects = [
    {
      title: 'Digital Payment Platform Transformation',
      company: 'Bank of America',
      description: 'Led the implementation of modern payment gateways (Zelle, Stripe) for digital banking platform, handling millions of transactions monthly.',
      icon: <CreditCard className="w-8 h-8" />,
      outcomes: [
        '30% increase in transaction processing speed',
        'PCI DSS Level 1 compliance achieved',
        'Integrated with 10+ financial institutions',
      ],
      technologies: ['Zelle', 'Stripe', 'PCI DSS', 'Microservices', 'Azure'],
      color: 'from-blue-600 to-blue-700',
    },
    {
      title: 'Enterprise Cloud Migration',
      company: 'Bank of America & HPE',
      description: 'Orchestrated Azure cloud migration for legacy banking systems, including data warehouse modernization and hybrid architecture implementation.',
      icon: <Cloud className="w-8 h-8" />,
      outcomes: [
        'Migrated 50+ applications to Azure',
        '40% reduction in infrastructure costs',
        'Implemented CI/CD pipelines for 20+ teams',
      ],
      technologies: ['Azure', 'DevOps', 'Microservices', 'CI/CD', 'Docker'],
      color: 'from-blue-700 to-blue-800',
    },
    {
      title: 'AEM Member Portal Integration',
      company: 'Bank of America',
      description: 'Built sophisticated member portals using Adobe Experience Manager integrated with Salesforce Service Cloud and SAP S/4HANA.',
      icon: <Users className="w-8 h-8" />,
      outcomes: [
        '2M+ active users onboarded',
        '45% improvement in member engagement',
        'Real-time data synchronization achieved',
      ],
      technologies: ['AEM', 'Salesforce', 'SAP S/4HANA', 'API Integration'],
      color: 'from-blue-800 to-blue-900',
    },
    {
      title: 'Agile Transformation Program',
      company: 'Western Union',
      description: 'Led $5M program transformation from Waterfall to Agile methodology for 20+ member Web/Mobile development team.',
      icon: <TrendingUp className="w-8 h-8" />,
      outcomes: [
        '30% increase in team velocity',
        '25% improvement in sprint predictability',
        'Reduced time-to-market by 40%',
      ],
      technologies: ['Scrum', 'SAFe', 'JIRA', 'Agile Coaching'],
      color: 'from-indigo-600 to-indigo-700',
    },
    {
      title: 'PCI DSS Compliance Implementation',
      company: 'USAA & Western Union',
      description: 'Ensured PCI DSS/PA DSS compliance for digital payment platforms, implementing comprehensive security controls and audit processes.',
      icon: <Shield className="w-8 h-8" />,
      outcomes: [
        'Achieved PCI DSS Level 1 certification',
        'Zero security breaches recorded',
        'Reduced compliance audit time by 35%',
      ],
      technologies: ['PCI DSS', 'Security Architecture', 'Audit', 'Risk Management'],
      color: 'from-indigo-700 to-indigo-800',
    },
    {
      title: 'Healthcare Data Modernization',
      company: 'Anthem & HPE',
      description: 'Modernized data warehouse from legacy ETL to cloud-native analytics for healthcare payer systems and member portals.',
      icon: <Database className="w-8 h-8" />,
      outcomes: [
        'Processed 10M+ healthcare records',
        '60% faster data processing',
        'Real-time analytics enabled',
      ],
      technologies: ['Azure', 'ETL', 'Data Analytics', 'Healthcare Systems'],
      color: 'from-indigo-800 to-indigo-900',
    },
  ];

  return (
    <section id="projects" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="projects-title">
            Featured Projects
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
          <p className="mt-4 text-xl text-gray-600">Transformative initiatives driving business value and innovation</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group"
              data-testid={`project-card-${index}`}
            >
              {/* Header with Gradient */}
              <div className={`bg-gradient-to-br ${project.color} text-white p-6`}>
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  {project.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                <p className="text-sm text-white/80">{project.company}</p>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-gray-700 mb-4">{project.description}</p>

                {/* Outcomes */}
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">Key Outcomes:</h4>
                  <ul className="space-y-1">
                    {project.outcomes.map((outcome, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-blue-600 mt-1">✓</span>
                        <span>{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Technologies */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;